import { generateId } from './showId';

const SSP_VISIBILITY_URL = 'https://ssp.24smi.net/rtb/v2/collect_teaser';

export const hasSspVisibilitySource = teaser => Boolean(
    teaser && teaser._ssp_source === true
);

export const hasCanonicalVisibilityIdentity = (teaser, showId) => Boolean(
    hasSspVisibilitySource(teaser) &&
    showId &&
    teaser.bid_uuid &&
    teaser.request_id &&
    teaser.visibility_context &&
    typeof teaser.bidder_user_id === 'number' &&
    (teaser.test === 0 || teaser.test === 1) &&
    typeof teaser.entrypoint === 'number' &&
    teaser.entrypoint >= 1 &&
    teaser.entrypoint <= 3 &&
    (teaser.delayed_insert === 0 || teaser.delayed_insert === 1)
);

const canonicalGroupKey = teaser => [
    teaser.request_id,
    teaser.test,
    teaser.entrypoint,
    teaser.delayed_insert
].join('|');

export const sendCanonicalVisibility = ({
    teasers,
    blockId,
    showId,
    onUnsupported = () => {},
    XMLHttpRequestImpl = typeof XMLHttpRequest === 'undefined' ? null : XMLHttpRequest
}) => {
    if (!XMLHttpRequestImpl || !teasers.length) {
        onUnsupported();
        return;
    }

    const first = teasers[0];
    const xhr = new XMLHttpRequestImpl();
    let fallbackSent = false;
    const fallback = () => {
        if (!fallbackSent) {
            fallbackSent = true;
            onUnsupported();
        }
    };

    xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4 || (xhr.status >= 200 && xhr.status < 300)) {
            return;
        }
        if ([0, 404, 405, 415, 501].includes(xhr.status)) {
            fallback();
        }
    };
    xhr.onerror = fallback;
    xhr.ontimeout = fallback;

    try {
        xhr.open('POST', SSP_VISIBILITY_URL, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.timeout = 5000;
        xhr.send(JSON.stringify({
            obj: Number(blockId),
            template_id: 0,
            bidder_ids: teasers.map(teaser => Number(teaser.bidder_id)),
            bidder_cids: teasers.map(teaser => String(teaser.bidder_cid)),
            creative_ids: teasers.map(teaser => String(teaser.crid)),
            bid_uuids: teasers.map(teaser => teaser.bid_uuid),
            bidder_user_ids: teasers.map(teaser => Number(teaser.bidder_user_id)),
            visibility_contexts: teasers.map(teaser => teaser.visibility_context),
            request_id: first.request_id,
            visibility_event_id: generateId(),
            test: first.test,
            entrypoint: first.entrypoint,
            delayed_insert: first.delayed_insert,
            show_id: showId,
            chash: first._ssp_chash || ''
        }));
    } catch (error) {
        fallback();
    }
};

export const sendLegacyVisibility = ({
    teasers,
    blockId,
    showId,
    fetchImpl = typeof fetch === 'undefined' ? null : fetch
}) => {
    const legacyTeasers = teasers.filter(teaser => (
        hasSspVisibilitySource(teaser) &&
        teaser.bidder_id !== undefined &&
        teaser.bidder_cid !== undefined &&
        teaser.crid !== undefined
    ));
    if (!fetchImpl || !showId || !legacyTeasers.length) {
        return;
    }

    const params = new URLSearchParams({
        obj: String(blockId),
        show_id: String(showId),
        bidder_ids: legacyTeasers.map(teaser => Number(teaser.bidder_id)).join(','),
        bidder_cids: legacyTeasers.map(teaser => String(teaser.bidder_cid)).join(','),
        creative_ids: legacyTeasers.map(teaser => String(teaser.crid)).join(','),
        chash: legacyTeasers[0]._ssp_chash || ''
    });

    fetchImpl(`${SSP_VISIBILITY_URL}?${params.toString()}`, {
        method: 'GET',
        mode: 'no-cors',
        keepalive: true
    }).catch(() => {});
};

export const sendSspVisibility = ({
    teasers,
    blockId,
    showId,
    sendCanonical = sendCanonicalVisibility,
    sendLegacy = sendLegacyVisibility
}) => {
    const canonicalGroups = {};
    const legacyTeasers = [];

    teasers.forEach(teaser => {
        if (!hasSspVisibilitySource(teaser)) {
            return;
        }
        if (!hasCanonicalVisibilityIdentity(teaser, showId)) {
            legacyTeasers.push(teaser);
            return;
        }

        const key = canonicalGroupKey(teaser);
        if (!canonicalGroups[key]) {
            canonicalGroups[key] = [];
        }
        canonicalGroups[key].push(teaser);
    });

    Object.keys(canonicalGroups).forEach(key => {
        const group = canonicalGroups[key];
        sendCanonical({
            teasers: group,
            blockId,
            showId,
            onUnsupported: () => sendLegacy({
                teasers: group,
                blockId,
                showId
            })
        });
    });

    if (legacyTeasers.length) {
        sendLegacy({
            teasers: legacyTeasers,
            blockId,
            showId
        });
    }
};
